import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Container, Flex, Box } from '../../design-system';
import styled from 'styled-components';
import config from '../../config';
import Alert from '../../helpers/alert';
import Logo from '../../components/Logo';
import SignUp from './SignUp';
import SelectPlan from './SelectPlan';
import CreditCardDetails from './CreditCardDetails';
import { Session, ACTIONS } from '../../contexts/sessionContext';
import { ERR_MSG } from '../../globalConstants';

const stripePromise = loadStripe(config.STRIPE_KEY);

const Wrapper = styled(Container)`
  background-color: ${(p) => p.theme.colors.greyDark};
`;

const MainContainer = styled(Box)`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  background-color: ${(p) => p.theme.colors.greyLightest};
`;

const Brand = styled(Flex)`
  justify-content: center;
  a {
    text-decoration: none;
  }
`;

const Register = () => {
  const { dispatch } = useContext(Session);
  const [newUser, setNewUser] = useState<{
    user: any;
    plan: string;
    creditCardInfo?: any;
  }>({
    user: null,
    plan: 'silver',
  });
  const [step, changeStep] = useState(1);
  const [registrationIntent, setRegistrationIntent] = useState<any>(null);

  const fetchSetupCredentials = async () => {
    try {
      const {
        data: { intent },
      } = await axios.get(`/auth/register`);
      setRegistrationIntent(intent);
      await axios.post('/auth/ustats', { trackingStage: 'started' });
    } catch (err) {
      // Extract specific error message from axios response
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        ERR_MSG;

      Alert.error(errorMessage);
    }
  };
  useEffect(() => {
    fetchSetupCredentials();
  }, []);

  const onUserSubmit = async (values?: any) => {
    setNewUser({ ...newUser, user: values });
    const payload = {
      ...values,
      trackingStage: 'basic',
    };
    try {
      await axios.post('/auth/ustats', payload);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        ERR_MSG;

      Alert.error(errorMessage);
    }

    // Advance to the credit-card step. The actual /auth/register call happens
    // in onAccountSubmit (CreditCardDetails), which has the payment details.
    changeStep(2);
  };

  const onAccountSubmit = async (values?: any) => {
    try {
      const payload = {
        ...newUser,
        trackingStage: 'credit',
        creditCardInfo: {
          ...values,
          turnstile_token: newUser.user?.turnstile_token,
        },
      };
      await axios.post('/auth/ustats', payload);

      console.log(payload);
      const {
        data: { user },
      } = await axios.post('/auth/register', payload);
      dispatch({ type: ACTIONS.BAPTIZE_USER, value: user });
      Alert.success('Welcome To Grantholster!');
    } catch (err) {
      // Extract specific error message from axios response
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        ERR_MSG;

      Alert.error(errorMessage);
      fetchSetupCredentials();
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
    }
  };

  const renderMainSection = () => {
    switch (step) {
      case 1:
        return <SignUp initialValues={newUser.user} onSubmit={onUserSubmit} />;

      case 2:
        return (
          <CreditCardDetails
            initialValues={newUser.creditCardInfo}
            onSubmit={onAccountSubmit}
            onBackClick={() => changeStep(1)}
            intent={registrationIntent}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Wrapper fluid gutter={100}>
      <MainContainer>
        <Brand p={10}>
          <Logo width="10.5rem" />
        </Brand>
        <Elements stripe={stripePromise}>
          <div>{renderMainSection()}</div>
        </Elements>
      </MainContainer>
    </Wrapper>
  );
};

export default Register;
